import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import { isAdminSelector, getCustomBlocksGroupSelector } from 'store/selectors';
import { getCustomBlocksGroup } from 'store/actions';
import { useCodemirror } from 'hooks';
import CodeMirrorHeader from 'components/CodeMirrorHeader';
import CodeMirrorAllButtons from 'components/CodeMirrorAllButtons';
import Button from 'components/Button';
import TextField from 'components/Forms/TextField';
import Select from 'components/Forms/Select';
import FormControl from 'components/Forms/FormControl';
import Label from 'components/Forms/Label';
import './BlockModal.sass';
import cx from 'classnames';

function BlockModal({
  block,
  isBlocksLoading,
  onSaveBlock,
  onClose,
  templateId,
  editor,
}) {
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      name: block?.name,
      description: block?.description,
    },
  });

  const intl = useIntl();
  const dispatch = useDispatch();
  const htmlEditorRef = useRef();
  const cssEditorRef = useRef();

  const [html, setHtml] = useState(block?.html);
  const [css, setCss] = useState(block?.css);

  const isAdmin = useSelector(isAdminSelector);
  const categoriesNames = useSelector(getCustomBlocksGroupSelector);

  const htmlEditor = useCodemirror(htmlEditorRef, {
    value: html,
    mode: 'htmlmixed',
  });

  const cssEditor = useCodemirror(cssEditorRef, {
    value: `${css.indexOf('<style') >= 0 ? css : `<style>${css}</style>`}`,
    mode: 'css',
  });

  useEffect(() => {
    dispatch(getCustomBlocksGroup()).catch(() => {});
  }, [dispatch]);

  useEffect(() => {
    const htmlEditorInstance = htmlEditor.current;
    const cssEditorInstance = cssEditor.current;

    const onHtmlChange = (cm) => {
      setHtml(cm.getValue());
    };

    const onCssChange = (cm) => {
      setCss(cm.getValue());
    };

    htmlEditorInstance.on('change', onHtmlChange);
    cssEditorInstance.on('change', onCssChange);

    return () => {
      htmlEditorInstance.off('change', onHtmlChange);
      cssEditorInstance.off('change', onCssChange);
    };
  }, [htmlEditor, cssEditor]);

  const handleSave = (values) => {
    const payload = {
      id: block?.id,
      type: 'email',
      ...values,
      html: htmlEditor.current.getValue(),
      css: cssEditor.current.getValue(),
      category: null,
      templateId,
    };

    if (values.category && values.category !== 'My modules') {
      const selectedCategory = categoriesNames?.filter(
        (item) => item.id === values.category,
      );

      const { id, name, group, type, hidden } = selectedCategory[0];

      payload.category = {
        id,
        name,
        group,
        type,
        hidden,
      };
    }

    onSaveBlock(payload);
  };

  return (
    <form className="block-modal__form" onSubmit={handleSubmit(handleSave)}>
      <div className="block-modal__buttons">
        <Button
          type="submit"
          className="block-modal__button"
          variant="contained"
          disabled={isBlocksLoading}
          loading={isBlocksLoading}>
          {intl.formatMessage({ id: 'button.save' })}
        </Button>
        <Button type="button" className="block-modal__button" onClick={onClose}>
          {intl.formatMessage({ id: 'button.cancel' })}
        </Button>
      </div>
      <div className="block-modal__content">
        <div className="block-modal__row">
          <div
            className={cx('block-modal__row__inner', {
              'block-modal__row__inner-full': !isAdmin,
            })}>
            <TextField
              {...register('name', {
                required: true,
              })}
              label={intl.formatMessage({ id: 'block-modal.name.label' })}
              helperText={
                errors.name
                  ? intl.formatMessage({ id: 'block-modal.name.error' })
                  : intl.formatMessage({
                      id: 'block-modal.name.helper-text',
                    })
              }
              placeholder={intl.formatMessage({
                id: 'block-modal.name.placeholder',
              })}
              error={Boolean(errors.name)}
              fullWidth
            />
            <TextField
              {...register('description')}
              label={intl.formatMessage({
                id: 'block-modal.description.label',
              })}
              placeholder={intl.formatMessage({
                id: 'block-modal.description.placeholder',
              })}
              helperText={intl.formatMessage({
                id: 'block-modal.description.helper-text',
              })}
              fullWidth
            />
          </div>
          {isAdmin && (
            <div className="block-modal__row__inner block-modal__row__category">
              <Select
                {...register('category', {
                  required: true,
                })}
                error={Boolean(errors.category)}
                label={intl.formatMessage({
                  id: 'block-modal.category.label',
                })}
                fullWidth
                defaultValue={block?.category?.id}
                helperText={
                  errors.category?.message ||
                  intl.formatMessage({
                    id: 'block-modal.category.helper-text',
                  })
                }>
                <option value="My modules">
                  {intl.formatMessage({
                    id: 'block-modal.category.placeholder',
                  })}
                </option>
                {categoriesNames?.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </Select>
            </div>
          )}
        </div>
        <div className="block-modal__row">
          <FormControl className="block-modal__editor-wrapper">
            <CodeMirrorHeader
              title="HTML"
              htmlEditor={htmlEditor}
              cssEditor={cssEditor}
              activeHtml
            />
            <div className="block-modal__editor" ref={htmlEditorRef} />
          </FormControl>
          <FormControl className="block-modal__editor-wrapper">
            <CodeMirrorHeader title="CSS" cssEditor={cssEditor} activeCss />
            <div className="block-modal__editor" ref={cssEditorRef} />
          </FormControl>
        </div>
        <div className="block-modal__row">
          <CodeMirrorAllButtons
            editor={editor}
            htmlEditor={htmlEditor}
            cssEditor={cssEditor}
            disableUpload
          />
        </div>
        <div className="block-modal__row">
          <FormControl fullWidth className="block-modal__preview-wrapper">
            <Label>Preview</Label>
            <iframe
              className="block-modal__preview"
              title="custom block preview"
              srcDoc={`
              ${html}
              ${css.indexOf('<style') >= 0 ? css : `<style>${css}</style>`}
            `}
            />
          </FormControl>
        </div>
      </div>
    </form>
  );
}

BlockModal.propTypes = {
  block: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    category: PropTypes.object,
    description: PropTypes.string,
    html: PropTypes.string.isRequired,
    css: PropTypes.string.isRequired,
  }),
  templateId: PropTypes.string.isRequired,
  editor: PropTypes.object,
  isBlocksLoading: PropTypes.bool.isRequired,
  onSaveBlock: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default BlockModal;
